import { Link, useParams } from 'react-router-dom';
import { Shield, ArrowLeft, Clock } from 'lucide-react';
import AppFooter from '../components/AppFooter';
import { GUIDES, getGuide } from '../data/guides';

function Block({ item }) {
  if (typeof item === 'string') return <p>{item}</p>;
  if (item.list) {
    return (
      <ul className="list-disc pl-6 space-y-2">
        {item.list.map((li, i) => (
          <li key={i}>{li}</li>
        ))}
      </ul>
    );
  }
  if (item.letter) {
    return (
      <pre className="whitespace-pre-wrap bg-[#F5F5F5] border border-[#36454F]/10 rounded-sm p-5 text-[13px] leading-relaxed font-mono overflow-x-auto">
        {item.letter}
      </pre>
    );
  }
  return null;
}

export default function GuidePage() {
  const { slug } = useParams();
  const guide = getGuide(slug);
  const others = GUIDES.filter((g) => g.slug !== slug);

  return (
    <div className="min-h-screen bg-[#F5F5F5]" data-testid="guide-page">
      <header className="glass-header sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <Link to="/" className="flex items-center" data-testid="home-link">
              <Shield className="w-8 h-8 text-[#B76E79]" strokeWidth={1.5} />
              <span className="ml-2 font-['Playfair_Display'] text-xl font-semibold text-[#36454F]">
                Selah Slate&trade;
              </span>
            </Link>
            <Link
              to="/resources"
              className="text-sm text-[#36454F]/70 hover:text-[#36454F] font-['Manrope'] flex items-center"
              data-testid="back-link"
            >
              <ArrowLeft className="w-4 h-4 mr-1" strokeWidth={1.5} />
              All guides
            </Link>
          </div>
        </div>
      </header>

      <main className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-10">
        {!guide ? (
          <div className="text-center py-20" data-testid="guide-not-found">
            <p className="font-['Playfair_Display'] text-2xl text-[#36454F] mb-4">Guide not found</p>
            <Link to="/resources" className="text-[#B76E79] font-['Manrope'] underline">
              Browse all guides
            </Link>
          </div>
        ) : (
          <>
            <div className="mb-8 animate-fade-in-up">
              <div className="flex items-center gap-3 mb-3 text-xs font-['Manrope']">
                <span className="px-2 py-0.5 rounded bg-[#B76E79]/10 text-[#B76E79] font-semibold uppercase tracking-wider">
                  {guide.category}
                </span>
                <span className="flex items-center text-[#36454F]/40">
                  <Clock className="w-3.5 h-3.5 mr-1" strokeWidth={1.5} />
                  {guide.readTime}
                </span>
              </div>
              <h1 className="font-['Playfair_Display'] text-3xl sm:text-4xl font-semibold text-[#36454F] leading-tight">
                {guide.title}
              </h1>
              <p className="mt-3 text-[#36454F]/60 font-['Manrope'] leading-relaxed">{guide.description}</p>
            </div>

            <article
              className="bg-white border border-[#36454F]/10 rounded-sm p-6 sm:p-10 space-y-8 font-['Manrope'] text-[#36454F]/85 leading-relaxed"
              data-testid="guide-article"
            >
              {guide.sections.map((s, i) => (
                <section key={i}>
                  <h2 className="font-['Playfair_Display'] text-xl font-semibold text-[#36454F] mb-3">{s.h}</h2>
                  <div className="space-y-3 text-[15px]">
                    {s.body.map((item, j) => (
                      <Block key={j} item={item} />
                    ))}
                  </div>
                </section>
              ))}

              <div className="pt-2 border-t border-[#36454F]/10">
                <p className="text-xs text-[#36454F]/45 italic">
                  Selah Slate is a free educational tool from Selah &amp; Stone Legacy Holdings LLC — not a credit
                  repair organization, law firm, or financial advisor. This guide is general information, not legal advice.
                </p>
              </div>
            </article>

            <div className="mt-8 p-6 sm:p-8 bg-[#36454F] rounded-sm text-center animate-fade-in-up" data-testid="guide-cta">
              <p className="font-['Playfair_Display'] text-xl font-semibold text-[#E0BFB8] mb-2">
                Put this guide into action — free
              </p>
              <p className="text-sm text-white/70 font-['Manrope'] mb-5 max-w-md mx-auto">
                Upload your credit report and Selah Slate drafts your dispute letters, tracks the 30-day clock,
                and guides every next step.
              </p>
              <Link
                to="/register"
                data-testid="guide-cta-register"
                className="inline-block px-6 py-3 bg-[#B76E79] text-white text-sm font-['Manrope'] font-semibold rounded-sm hover:bg-[#a35d68] transition-colors"
              >
                Start free — no credit card
              </Link>
            </div>

            <div className="mt-10" data-testid="related-guides">
              <p className="text-xs uppercase tracking-wider text-[#36454F]/40 font-['Manrope'] font-semibold mb-4">
                More guides
              </p>
              <div className="space-y-3">
                {others.map((g) => (
                  <Link
                    key={g.slug}
                    to={`/resources/${g.slug}`}
                    className="block bg-white border border-[#36454F]/10 rounded-sm p-4 hover:border-[#B76E79]/40 transition-colors"
                  >
                    <span className="font-['Manrope'] text-sm font-semibold text-[#36454F]">{g.title}</span>
                  </Link>
                ))}
              </div>
            </div>
          </>
        )}
      </main>

      <AppFooter />
    </div>
  );
}
